
import { ReactNode } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CheckIcon, Trash } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";
import { formatDistanceToNow } from "date-fns";
import { ru } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface NotificationsPopoverProps {
  children: ReactNode;
}

const NotificationsPopover = ({ children }: NotificationsPopoverProps) => {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const { t, i18n } = useTranslation();

  const getTypeStyles = (type: string) => {
    switch (type) {
      case "success": return "bg-green-100 text-green-800";
      case "error": return "bg-red-100 text-red-800";
      case "warning": return "bg-yellow-100 text-yellow-800";
      default: return "bg-blue-100 text-blue-800";
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), {
        addSuffix: true,
        locale: i18n.language === 'ru' ? ru : undefined
      });
    } catch (e) {
      return t('notifications.popup.recently');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex justify-between items-center border-b p-3">
          <h3 className="font-medium">{t('notifications.popup.title')}</h3>
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
            >
              <span className="text-xs">{t('notifications.popup.mark_all_read')}</span>
            </Button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-3 border-b flex items-start gap-2 ${notification.read ? 'opacity-70' : ''}`}
              >
                <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${getTypeStyles(notification.type)}`}>
                  <CheckIcon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    {/* Assuming notifications messages are already translated or dynamic data */}
                    {notification.message}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(notification.createdAt)}
                  </p>
                </div>
                {!notification.read && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0 h-8 w-8"
                    onClick={() => markAsRead(notification.id)}
                  >
                    <span className="sr-only">{t('notifications.popup.mark_read')}</span>
                    <div className="h-2 w-2 bg-brand-500 rounded-full"></div>
                  </Button>
                )}
              </div>
            ))
          ) : (
            <div className="p-6 text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Trash size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('notifications.popup.empty')}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsPopover;
